/* eslint-disable @typescript-eslint/naming-convention */

import { type App } from '~/app';

import JsController from '../js-controller';
import { info } from 'node:console';
import { set } from 'lodash';
import { doc } from 'prettier';

export default (app: App) => {
	const controller = new JsController(app, 'Product Details');
	controller.addMountFunction(() => {
		mountProductDetails(controller);
	});

	return controller.init();
};

export function mountProductDetails(controller: JsController) {
	const isMobileScreen = window.innerWidth <= 768 ? true : false;

	const textContainers = document.querySelectorAll<HTMLElement>(`.product-detail-text-container`);

	for (const textContainer of textContainers) {
		const text = textContainer.querySelector('.product-detail-text');
		if (text) {
			const style = window.getComputedStyle(text);
			const lineHeight = Number.parseFloat(style.lineHeight);
			const containerHeight = text.clientHeight;
			const lines = containerHeight / lineHeight;

			if (lines > 8) {
				const readMore = textContainer.querySelector<HTMLElement>('.product-detail-read-more');
				const gradient = textContainer.querySelector('.product-detail-text-gradient');
				if (readMore && !readMore.dataset.listenerAdded && gradient) {
					readMore.dataset.listenerAdded = 'true';

					readMore.classList.remove('hidden');
					gradient.classList.remove('hidden');

					const inner = readMore.querySelector('.product-detail-read-more-inner');
					const icon = readMore.querySelector('.icon-item');

					controller.addListener(inner!, 'click', () => {
						const gradient = textContainer.querySelector('.product-detail-text-gradient');
						const readMoreText = inner!.querySelector('.read-more-text');

						if (readMoreText!.textContent?.trim() === 'Read More') {
							// Actions for Read More
							gradient?.classList.add('hidden');
							text.classList.remove('overflow-hidden', 'max-h-52');
							text.classList.add('mb-10');
							readMoreText!.textContent = 'Read Less';
							icon!.classList.add('rotate-180'); // Rotate icon
						} else {
							// Actions for Read Less
							gradient?.classList.remove('hidden');
							text.classList.add('overflow-hidden', 'max-h-52');
							text.classList.remove('mb-10');
							readMoreText!.textContent = 'Read More';
							icon!.classList.remove('rotate-180'); // Revert icon rotation
						}
					});
				}
			}
		}
	}

	const builderSections = document.querySelectorAll<HTMLElement>('.product-detail-builder-section');

	for (const builderSection of builderSections) {
		const builderList = builderSection.querySelector('.product-detail-builder-list');
		const lines = builderList?.querySelectorAll('li').length;
		const builderReadMore = builderSection.querySelector<HTMLElement>('.product-detail-builder-read-more');
		const builderListContainer = builderSection.querySelector('.product-details-builder-container');
		const gradient = builderListContainer!.querySelector('.product-detail-builder-gradient');

		if (lines && lines > 4 && builderReadMore && !builderReadMore?.dataset.listenerAdded) {
			builderReadMore?.classList.remove('hidden');
			builderReadMore.dataset.listenerAdded = 'true'; // Set a flag that the listener is added
			gradient?.classList.add('bg-gradient-to-t', 'from-primary-background');

			const inner = builderReadMore?.querySelector('.product-detail-builder-read-more-inner');
			const icon = builderReadMore?.querySelector('.icon-item');

			controller.addListener(inner!, 'click', () => {
				const readMoreText = inner!.querySelector('.read-more-text');

				if (readMoreText!.textContent!.trim() === 'Read More') {
					// Actions for Read More
					gradient?.classList.remove('bg-gradient-to-t', 'from-primary-background');
					builderListContainer!.classList.remove('overflow-hidden', 'max-h-96');
					readMoreText!.textContent = 'Read Less';
					icon!.classList.add('rotate-180'); // Rotate icon
				} else {
					// Actions for Read Less
					gradient?.classList.add('bg-gradient-to-t', 'from-primary-background');
					builderListContainer!.classList.add('overflow-hidden', 'max-h-96');
					readMoreText!.textContent = 'Read More';
					icon!.classList.remove('rotate-180'); // Revert icon rotation
				}
			});
		}
	}

	const featureSections = document.querySelectorAll<HTMLElement>('.product-details-features-section');

	for (const featureSection of featureSections) {
		const LLCIconList = featureSection.querySelector<HTMLElement>('.LLC-features-icons');
		const LLCIconCount = LLCIconList?.querySelectorAll('.features-icon').length;
		const LLCReadMore = featureSection.querySelector<HTMLElement>('.LLC-read-more');

		const MPCIconList = featureSection.querySelector<HTMLElement>('.MPC-features-icons');
		const MPCIconCount = MPCIconList?.querySelectorAll('.features-icon').length;
		const MPCReadMore = featureSection.querySelector<HTMLElement>('.MPC-read-more');

		if (LLCReadMore && LLCIconCount && LLCIconCount > 12 && !LLCReadMore!.dataset.listenerAdded) {
			LLCReadMore.dataset.listenerAdded = 'true';

			const icons = LLCIconList?.querySelectorAll<HTMLElement>('.features-icon');
			const count = isMobileScreen ? 10 : 12;

			// Initially hide icons beyond the count
			for (let index = count; index < icons.length; index++) {
				icons[index].classList.add('hidden');
			}

			LLCReadMore.classList.remove('hidden');
			LLCReadMore.classList.add('flex');

			controller.addListener(LLCReadMore, 'click', () => {
				const readMoreText = LLCReadMore.querySelector('.read-more-text');
				const icon = LLCReadMore.querySelector('.icon-item');

				if (readMoreText!.textContent!.trim() === 'View All') {
					// Show all icons
					for (const icon of icons) {
						icon.classList.remove('hidden');
					}

					readMoreText!.textContent = 'View Less';
					icon!.classList.add('rotate-180'); // Rotate icon
				} else {
					// Hide extra icons
					for (let index = count; index < icons.length; index++) {
						icons[index].classList.add('hidden');
					}

					readMoreText!.textContent = 'View All';
					icon!.classList.remove('rotate-180'); // Revert icon rotation
				}
			});
		}

		if (MPCReadMore && MPCIconCount && MPCIconCount > 6 && !MPCReadMore.dataset.listenerAdded) {
			MPCReadMore.dataset.listenerAdded = 'true';

			MPCReadMore.classList.remove('hidden');
			MPCReadMore.classList.add('flex');

			const icons = MPCIconList?.querySelectorAll<HTMLElement>('.features-icon');
			const count = isMobileScreen ? 4 : 6;

			for (let index = count; index < icons!.length; index++) {
				icons![index].classList.add('hidden');
			}

			controller.addListener(MPCReadMore, 'click', () => {
				const readMoreText = MPCReadMore.querySelector('.read-more-text');
				const icon = MPCReadMore.querySelector('.icon-item');

				if (readMoreText!.textContent!.trim() === 'Read More') {
					for (const icon of icons!) {
						icon.classList.remove('hidden');
					}

					readMoreText!.textContent! = 'Read Less';
					icon!.classList.add('rotate-180');
				} else {
					for (let index = count; index < icons!.length; index++) {
						icons![index].classList.add('hidden');
					}

					readMoreText!.textContent = 'Read More';
					icon!.classList.remove('rotate-180');
				}
			});
		}
	}

	const bonusBars = document.querySelectorAll<HTMLElement>(`.bonus-bar`);

	for (const bonusBar of bonusBars) {
		controller.addListener(bonusBar, 'click', () => {
			if (bonusBar.getAttribute('aria-expanded') == 'true') {
				const chevron = bonusBar.querySelector<HTMLElement>('.bonus-chevron');
				chevron?.classList.add('rotate-180');
			} else {
				const chevron = bonusBar.querySelector<HTMLElement>('.bonus-chevron');
				chevron?.classList.remove('rotate-180');
			}
		});
	}

	const featureTags = document.querySelectorAll<HTMLElement>(`.feature-tag`);
	const toolTips = document.querySelectorAll<HTMLElement>(`.feature-tooltip`);
	for (const featureTag of featureTags) {
		controller.addListener(featureTag, 'click', () => {
			for (const tooltip of toolTips) {
				tooltip.classList.add('hidden');
				if (tooltip.classList.contains(featureTag.id)) {
					tooltip.classList.remove('hidden');
				}
			}
		});
	}

	function checkInfoBubbleInViewPort() {
		const infoBubble = document.querySelectorAll<HTMLElement>('.information-bubble');
		infoBubble.forEach((bubble) => {
			setTimeout(() => {
				const bubbleRect = bubble.getBoundingClientRect();

				if (bubbleRect.right > window.innerWidth) {
					bubble.classList.remove('left');
					bubble.classList.add('right');
				} else if (bubbleRect.left < 0) {
					bubble.classList.remove('right');
					bubble.classList.add('left');
				}
			}, 500);
		});
	}
	checkInfoBubbleInViewPort();
	window.addEventListener('resize', checkInfoBubbleInViewPort);
}
